/**
 * Subscription Card Component
 * Shows current subscription status with manage options
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CreditCard, Crown, ExternalLink, Loader2, Sparkles } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useAuthStore } from '../../../../stores/auth-store';
import { api } from '../../../../services/api';

type SubscriptionSource = 'stripe' | 'apple' | 'google' | 'none';

interface TierInfo {
  id: string;
  name: string;
  description: string;
}

const TIER_INFO: Record<string, TierInfo> = {
  free: { id: 'free', name: 'Free', description: 'Basic features for casual users' },
  pro: { id: 'pro', name: 'Pro', description: 'Enhanced features for power users' },
  pro_plus: { id: 'pro_plus', name: 'Pro+', description: 'All features unlocked' },
};

export function SubscriptionCard() {
  const { t } = useLanguage();
  const { user } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);

  const currentTier = user?.subscription_tier || 'free';
  const tierInfo = TIER_INFO[currentTier] || TIER_INFO.free;

  // Determine subscription source
  const subscriptionSource: SubscriptionSource = user?.subscription_source || 'none';

  // Get source badge info
  const getSourceBadge = () => {
    switch (subscriptionSource) {
      case 'stripe':
        return { label: 'Stripe', bg: 'bg-indigo-100', text: 'text-indigo-700' };
      case 'apple':
        return { label: t('settings.subscription.appStore', 'App Store'), bg: 'bg-gray-100', text: 'text-gray-700' };
      case 'google':
        return { label: t('settings.subscription.googlePlay', 'Google Play'), bg: 'bg-green-100', text: 'text-green-700' };
      default:
        return null;
    }
  };

  const sourceBadge = getSourceBadge();

  const handleManageSubscription = async () => {
    setIsLoading(true);
    try {
      const { url } = await api.getCustomerPortal();
      window.location.href = url;
    } catch (error) {
      console.error('Failed to open billing portal:', error);
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg hover:border-primary-200 transition-all duration-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <CreditCard className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">
                {t('settings.subscription.title', 'Subscription')}
              </h2>
              <p className="text-sm text-gray-500">
                {t('settings.subscription.subtitle', 'Your current plan and billing')}
              </p>
            </div>
          </div>

          {/* Tier badge */}
          <div className="flex items-center gap-2">
            {currentTier !== 'free' && (
              <div className="flex items-center gap-1 px-3 py-1.5 bg-primary-100 text-primary-700 rounded-full text-sm font-medium">
                <Crown className="w-4 h-4" />
                {tierInfo.name}
              </div>
            )}
            {sourceBadge && (
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${sourceBadge.bg} ${sourceBadge.text}`}>
                {sourceBadge.label}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Current plan info */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-2xl font-bold text-gray-900">{tierInfo.name}</p>
            <p className="text-sm text-gray-500">{tierInfo.description}</p>
          </div>
        </div>

        {/* App Store subscription notice */}
        {subscriptionSource === 'apple' && (
          <div className="p-4 bg-gray-50 rounded-xl mb-4">
            <p className="text-sm text-gray-600">
              {t('settings.subscription.appleNotice', 'Your subscription was purchased through the App Store.')}
            </p>
            <a
              href="https://apps.apple.com/account/subscriptions"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
            >
              {t('settings.subscription.manageAppStore', 'Manage in App Store')}
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        )}

        {/* Google Play subscription notice */}
        {subscriptionSource === 'google' && (
          <div className="p-4 bg-gray-50 rounded-xl mb-4">
            <p className="text-sm text-gray-600">
              {t('settings.subscription.googleNotice', 'Your subscription was purchased through Google Play.')}
            </p>
            <a
              href="https://play.google.com/store/account/subscriptions"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
            >
              {t('settings.subscription.manageGooglePlay', 'Manage in Google Play')}
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-wrap gap-3">
          {/* Stripe manage button */}
          {subscriptionSource === 'stripe' && (
            <button
              onClick={handleManageSubscription}
              disabled={isLoading}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all disabled:opacity-50"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ExternalLink className="w-4 h-4" />
              )}
              {t('settings.subscription.manageBilling', 'Manage Billing')}
            </button>
          )}

          {/* Upgrade button for free tier */}
          {currentTier === 'free' && (
            <Link
              to="/account/subscription"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 transition-all"
            >
              <Sparkles className="w-4 h-4" />
              {t('settings.subscription.upgrade', 'Upgrade Plan')}
            </Link>
          )}

          {/* View plans link for paid users */}
          {currentTier !== 'free' && (
            <Link
              to="/account/subscription"
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-all"
            >
              {t('settings.subscription.viewPlans', 'View Plans')}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
